import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { TOKEN_KEY, API_BASE } from '../config';

interface AuthContextType {
  token: string | null;
  playerId: number | null;
  syncing: boolean;
  login: (token: string) => void;
  finishSync: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [playerId, setPlayerId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const syncChecked = useRef(false);

  useEffect(() => {
    // Token aus URL-Param nach OAuth-Callback einlesen
    // Unterstützt sowohl Query-Parameter (?token=...) als auch Hash-Fragment (#admin/?token=...)
    let urlToken = null;
    
    // Zuerst Query-Parameter prüfen (normale URL)
    const params = new URLSearchParams(window.location.search);
    urlToken = params.get('token');
    
    // Falls nicht gefunden: Hash-Fragment prüfen (#page/?token=...)
    if (!urlToken && window.location.hash) {
      const hashContent = window.location.hash.substring(1); // # entfernen
      const hashParams = new URLSearchParams(hashContent.split('?')[1] || '');
      urlToken = hashParams.get('token');
    }
    
    if (urlToken) {
      login(urlToken);
      // Flag setzen: Background-Sync soll beim nächsten Dashboard-Load feuern
      localStorage.setItem('nww_sync_pending', '1');
      // URL bereinigen: Token entfernen, Hash beibehalten
      const cleanHash = window.location.hash.split('?')[0];
      window.history.replaceState({}, '', cleanHash || window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[0]));
        setPlayerId(payload.pid ?? null);
      } catch {
        setPlayerId(null);
      }
    }
  }, [token]);

  // When syncing, try to fetch /player/me to check if backend is ready
  useEffect(() => {
    if (!syncing || !token) return;
    syncChecked.current = true;

    let attempts = 0;
    const maxAttempts = 60;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_BASE}/player/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          clearInterval(interval);
          window.dispatchEvent(new Event('sync-done'));
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          window.dispatchEvent(new Event('sync-done'));
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          window.dispatchEvent(new Event('sync-done'));
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [syncing, token]);

  const finishSync = () => { setSyncing(false); syncChecked.current = false; };

  const login = (t: string) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setPlayerId(null);
    setSyncing(false);
  };

  return (
    <AuthContext.Provider value={{ token, playerId, syncing, login, finishSync, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
